import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Copy, Lock, Search } from "lucide-react";

export default function Library() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/");
      return;
    }
    (async () => {
      try {
        const r = await apiClient.get("/library");
        setItems(r.data.items || []);
      } catch (e) {
        const status = e?.response?.status;
        if (status === 402) setError("pro");
        else setError("error");
      }
    })();
  }, [user, loading, navigate]);

  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    return items.filter(
      (t) =>
        !s ||
        t.title.toLowerCase().includes(s) ||
        t.category.toLowerCase().includes(s) ||
        t.template.toLowerCase().includes(s)
    );
  }, [items, q]);

  if (error === "pro") {
    return (
      <div className="App min-h-screen">
        <Header />
        <main className="max-w-3xl mx-auto px-4 md:px-8 py-20 text-center">
          <Lock size={32} className="mx-auto text-[var(--primary,#2563EB)]" />
          <h1 className="font-display text-5xl md:text-6xl mt-5 leading-[1.05] tracking-tight">
            Library is an <em>AI Mastery</em> perk
          </h1>
          <p className="mt-4 text-[var(--ink-2)]">
            Battle-tested prompt templates for emails, meetings, resumes, spreadsheets, and more. AI Mastery unlocks the full library.
          </p>
          <Link
            to="/pricing"
            className="btn-accent mt-7"
            data-testid="library-upgrade-cta"
          >
            Unlock with AI Mastery
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className="App min-h-screen">
      <Header />
      <main className="max-w-5xl mx-auto px-4 md:px-8 py-12 pb-28 md:pb-12">
        <span className="kicker">AI Mastery · Templates</span>
        <h1 className="font-display text-5xl md:text-6xl mt-3 leading-[1.05] tracking-tight">Prompt <em>Library</em></h1>
        <div className="mt-7 relative">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--ink-2)]" />
          <input
            className="brut-input pl-10"
            placeholder="Search by title, category, or keyword…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="library-search-input"
          />
        </div>
        <div className="mt-8 grid md:grid-cols-2 gap-5">
          {filtered.map((t) => (
            <div key={t.id} className="brut p-6 brut-hover" data-testid={`library-card-${t.id}`}>
              <span className="pill pill-accent">{t.category}</span>
              <h3 className="font-display text-2xl mt-3 leading-tight">{t.title}</h3>
              <div className="mt-3 rounded-xl border border-white/5 bg-[var(--ink)] overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 border-b border-white/5">
                  <div className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-white/15" />
                    <span className="w-1.5 h-1.5 rounded-full bg-white/15" />
                    <span className="w-1.5 h-1.5 rounded-full bg-white/15" />
                  </div>
                  <span className="font-mono text-[0.62rem] text-white/40">template.txt</span>
                </div>
                <pre className="px-3.5 py-3 whitespace-pre-wrap font-mono text-[0.78rem] leading-[1.65] text-white/85 max-h-56 overflow-y-auto">
                  {t.template}
                </pre>
              </div>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(t.template);
                  setCopied(t.id);
                  setTimeout(() => setCopied(null), 1500);
                }}
                className="btn-ghost mt-4"
                style={{ padding: "0.45rem 0.9rem", fontSize: "0.8rem" }}
                data-testid={`library-copy-${t.id}`}
              >
                <Copy size={12} />
                {copied === t.id ? "Copied!" : "Copy template"}
              </button>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="text-center text-sm text-[var(--ink-2)] col-span-2 py-12">
              No templates match your search.
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
